"use client";

import { JSONUIProvider, Renderer, defineRegistry } from "@json-render/react";
import { type ReactNode, createContext, useContext } from "react";
import { type Spec, catalog } from "./catalog";

const ActivityContext = createContext<(id: string) => ReactNode>(() => null);
const ExerciseContext = createContext<(id: string) => ReactNode>(() => null);
const defaultHeading = (text: string) => <h2 className="stream-heading">{text}</h2>;
const HeadingContext = createContext<(text: string) => ReactNode>(defaultHeading);
function HostHeading({ text }: { text: string }) {
  return useContext(HeadingContext)(text);
}
const RevealContext = createContext<{
  report: (label: string, text: string, open: boolean) => void;
  isOpen: (label: string, text: string) => boolean;
}>({
  report: (_label: string, _text: string, _open: boolean) => {},
  isOpen: (_label: string, _text: string) => false,
});
function HostReveal({ label, text }: { label: string; text: string }) {
  const { report, isOpen } = useContext(RevealContext);
  return (
    <details
      className="stream-reveal"
      open={isOpen(label, text)}
      onToggle={(event) => report(label, text, event.currentTarget.open)}
    >
      <summary className="min-h-11 cursor-pointer py-3">{label}</summary>
      <p>{text}</p>
    </details>
  );
}
function HostExercise({ id }: { id: string }) {
  return useContext(ExerciseContext)(id);
}
function HostActivity({ id }: { id: string }) {
  return useContext(ActivityContext)(id);
}
const { registry } = defineRegistry(catalog, {
  components: {
    Stack: ({ children }) => <div className="stream-stack">{children}</div>,
    Panel: ({ children }) => <section className="stream-panel">{children}</section>,
    Heading: ({ props }) => <HostHeading text={String(props.text)} />,
    Text: ({ props }) => <p className="stream-text whitespace-pre-wrap">{String(props.text)}</p>,
    Table: ({ props }) => (
      <div className="stream-table overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr>
              {(props.headers as string[]).map((header, i) => (
                <th key={i}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(props.rows as string[][]).map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
    Reveal: ({ props }) => <HostReveal label={String(props.label)} text={String(props.text)} />,
    Activity: ({ props }) => <HostActivity id={String(props.activityId)} />,
    Exercise: ({ props }) => <HostExercise id={String(props.exerciseId)} />,
  },
});

export function StreamingRenderer({
  spec,
  activity,
  exercise = () => null,
  reveal = () => {},
  revealOpen = () => false,
  heading = defaultHeading,
}: {
  spec: Spec;
  activity: (id: string) => ReactNode;
  exercise?: (id: string) => ReactNode;
  reveal?: (label: string, text: string, open: boolean) => void;
  revealOpen?: (label: string, text: string) => boolean;
  heading?: (text: string) => ReactNode;
}) {
  return (
    <HeadingContext.Provider value={heading}>
      <ActivityContext.Provider value={activity}>
        <ExerciseContext.Provider value={exercise}>
          <RevealContext.Provider value={{ report: reveal, isOpen: revealOpen }}>
            <JSONUIProvider registry={registry}>
              <Renderer spec={spec} registry={registry} />
            </JSONUIProvider>
          </RevealContext.Provider>
        </ExerciseContext.Provider>
      </ActivityContext.Provider>
    </HeadingContext.Provider>
  );
}
